import { Building2, Edit2 } from 'lucide-react'
import { EMPRESA_STATUS_LABELS, EMPRESA_STATUS_LEGACY_LABELS } from '@/pages/empresasPageConstants'
import type { EmpresaDetailed } from '@/types'

interface EmpresasListTableProps {
  empresas: EmpresaDetailed[]
  loading?: boolean
  onSelect: (empresa: EmpresaDetailed) => void
}

const STATUS_BADGE_CLASS: Record<string, string> = {
  CLIENTE_ATIVO: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  PROSPECT: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  CHURN: 'bg-red-500/10 text-red-400 border-red-500/20',
  ativo: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  inativo: 'bg-[var(--glass-bg)] text-[var(--text-muted)] border-[var(--glass-border)]',
  prospecto: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  churned: 'bg-red-500/10 text-red-400 border-red-500/20',
}

function empresaDisplayName(empresa: EmpresaDetailed): string {
  return empresa.nome ?? empresa.razaoSocial ?? '—'
}

function empresaInitials(empresa: EmpresaDetailed): string {
  return empresaDisplayName(empresa)
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function EmpresasListTable({ empresas, loading, onSelect }: EmpresasListTableProps) {
  if (loading) {
    return (
      <div className='glass-card p-8 text-center'>
        <p className='text-[var(--font-color-muted)] text-sm'>Carregando empresas...</p>
      </div>
    )
  }

  if (empresas.length === 0) {
    return (
      <div className='glass-card p-12 text-center'>
        <Building2 size={48} className='mx-auto mb-3 text-[var(--text-muted)] opacity-50' />
        <p className='text-[var(--font-color-body)] font-medium'>Nenhuma empresa encontrada</p>
        <p className='text-[var(--font-color-muted)] text-sm mt-1'>Ajuste os filtros ou cadastre uma nova empresa</p>
      </div>
    )
  }

  return (
    <div className='glass-card p-0 overflow-hidden'>
      <div className='overflow-x-auto'>
        <table className='w-full'>
          <thead>
            <tr className='border-b border-[var(--glass-border)]'>
              {['Empresa', 'Segmento', 'Status', 'Métricas', 'Ações'].map((h) => (
                <th
                  key={h}
                  className='text-left px-4 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider'
                  style={{ fontSize: 'var(--font-size-label)' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {empresas.map((empresa) => (
              <tr
                key={empresa.id}
                className='border-b border-[var(--glass-border)] last:border-0 hover:bg-[var(--glass-bg)] transition-colors cursor-pointer'
                onClick={() => onSelect(empresa)}
              >
                <td className='px-4 py-3'>
                  <div className='flex items-center gap-3'>
                    <div className='w-8 h-8 rounded-lg bg-[var(--brand-primary-muted)] flex items-center justify-center shrink-0'>
                      <span className='text-[11px] font-bold text-[var(--brand-primary)]'>
                        {empresaInitials(empresa)}
                      </span>
                    </div>
                    <span
                      className='font-medium text-[var(--font-color-body)]'
                      style={{ fontSize: 'var(--font-size-sm)' }}
                    >
                      {empresaDisplayName(empresa)}
                    </span>
                  </div>
                </td>
                <td className='px-4 py-3'>
                  <span
                    className='text-[var(--font-color-muted)]'
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    {empresa.segmento ?? '—'}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <span
                    className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase border ${
                      STATUS_BADGE_CLASS[empresa.status] ?? STATUS_BADGE_CLASS.inativo
                    }`}
                  >
                    {EMPRESA_STATUS_LABELS[empresa.status] ?? EMPRESA_STATUS_LEGACY_LABELS[empresa.status] ?? empresa.status}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <div className='flex items-center gap-1.5 flex-wrap'>
                    {empresa.leads && empresa.leads.length > 0 && (
                      <span className='px-2 py-0.5 rounded-md text-[9px] font-bold uppercase bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border border-[var(--brand-primary)]/20'>
                        {empresa.leads.length} Leads
                      </span>
                    )}
                    {empresa.deals && empresa.deals.length > 0 && (
                      <span className='px-2 py-0.5 rounded-md text-[9px] font-bold uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'>
                        {empresa.deals.length} Deals
                      </span>
                    )}
                    {!empresa.leads?.length && !empresa.deals?.length && (
                      <span
                        className='text-[var(--font-color-muted)]'
                        style={{ fontSize: 'var(--font-size-sm)' }}
                      >
                        —
                      </span>
                    )}
                  </div>
                </td>
                <td className='px-4 py-3'>
                  <div className='flex items-center gap-1'>
                    <button
                      type='button'
                      className='p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--brand-primary)] hover:bg-[var(--glass-bg)] transition-colors'
                      onClick={(e) => {
                        e.stopPropagation()
                        onSelect(empresa)
                      }}
                      title='Editar empresa'
                      aria-label='Editar empresa'
                    >
                      <Edit2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
